import { css } from "@emotion/css";
import { Key, useContext, useRef, useState } from "react";
import { useLabel } from "react-aria";
import { ComboBoxProps } from "react-aria-components";
import { useTranslation } from "react-i18next";

import {
  GhostIconButton,
  SelectItem,
  useExternalScript,
} from "@certn/cascadia-react";

import { _ComboBox } from "./assets/_ComboBox";
import { _GoogleFooter } from "./assets/_GoogleFooter";
import { _Header } from "./assets/_Header";
import { _ListBox } from "./assets/_ListBox";
import { useAddressPredictions } from "./useAddressPredictions";
import { GoogleMapsAPIKeyContext } from "../../../context/GoogleMapsAPIKeyProvider";
import { HighlightText } from "../../../foundations/typography/HighlightText";
import { useRefOffsetWidthResize } from "../../../hooks/useRefOffsetWidthResize";
import { _Popover } from "../../_assets/_Popover";
import { _Text } from "../../_assets/_Text";
import { _Input } from "../_assets/_Input";
import { _InputWrapper } from "../_assets/_InputWrapper";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

interface AddressItem {
  id: string;
  description: string;
  textValue: string;
}

declare global {
  interface Window {
    initMap: any;
  }
}

window.initMap = function () {
  window.initMap = null;
};

export interface AddressTextFieldProps
  extends Omit<
    ComboBoxProps<AddressItem>,
    | "allowsCustomValue"
    | "children"
    | "footer"
    | "isLoading"
    | "items"
    | "onOpenChange"
    | "onSelectionChange"
    | "selectedKey"
  > {
  onAddressSelected: (value: google.maps.places.PlaceResult) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  errors?: FieldErrorsProps["children"];
  emptyMessage?: string;
  info?: React.ReactNode;
  region?: string;
  locale?: string;
  apiKey?: string;
  testID: string;
  selectedKey?: string;
  isDisabled?: boolean;
}

/*
 * To be used inside Formik Field, pass in onSelectionChange in order to
 * retrieve the address components for auto filling other fields
 * ******
 * <GoogleAddressSelectField
 *  onAddressSelected={(value: any[]) => console.log("onAddressSelected: ", value)
 * />
 * ******
 */
export function AddressTextField(props: AddressTextFieldProps) {
  const { t } = useTranslation();
  const { labelProps, fieldProps } = useLabel(props);
  const ref = useRef<HTMLDivElement>(null);
  const { inputWidth } = useRefOffsetWidthResize(ref);

  const sharedKey = useContext(GoogleMapsAPIKeyContext);

  const {
    description,
    placeholder,
    errors,
    region,
    locale,
    apiKey = sharedKey,
    testID,
    defaultInputValue,
    isDisabled,
  } = props;

  const [isOpen, setOpen] = useState(false);

  // Add Google API script to html body
  const state = useExternalScript(
    `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap&libraries=places`
  );

  const { getDetails, predictions } = useAddressPredictions({
    region,
    locale,
    input: props.inputValue || "",
  });

  const handleSelect = async (placeId: Key) => {
    if (typeof placeId !== "string") {
      return null;
    }

    try {
      const details = await getDetails(placeId);

      if (details) {
        props.onAddressSelected(details);
        setOpen(false);
      }
    } catch (e) {
      console.error("Error getting place information", e);
    }
  };

  const isLoading = state === "loading";

  const items = predictions.map((item) => ({
    id: item.place_id,
    description: item.description,
    textValue: item.structured_formatting.main_text,
  }));

  return (
    <div className={styles.wrapper}>
      {props.label && (
        <FormLabelRAC
          isRequired={props.isRequired}
          info={props.info}
          {...labelProps}
        >
          {props.label}
        </FormLabelRAC>
      )}
      <_ComboBox
        {...fieldProps}
        isInvalid={props.isInvalid}
        data-testid={`${testID}-combobox`}
        allowsCustomValue
        onSelectionChange={handleSelect}
        defaultInputValue={defaultInputValue}
        onInputChange={props.onInputChange}
        onOpenChange={setOpen}
        items={items}
        isDisabled={isDisabled}
      >
        <div ref={ref}>
          <_InputWrapper>
            <_Input
              onBlur={props.onBlur}
              isLoading={isLoading}
              placeholder={placeholder}
              data-testid={testID}
            />
          </_InputWrapper>
        </div>
        {description && (
          <_Text style="input/helper" color="text-weak" slot="description">
            {description}
          </_Text>
        )}
        <FieldErrors>{errors}</FieldErrors>
        <_Popover
          offset={8}
          isOpen={isOpen && predictions.length > 0}
          className={css({ width: `${inputWidth}px` })}
        >
          <div className={styles.header}>
            <_Header textStyle="summary/section-title">
              {t("Suggestions")}
            </_Header>
            <GhostIconButton icon="close" onPress={() => setOpen(false)} />
          </div>
          <_ListBox<AddressItem>>
            {(item) => (
              <SelectItem
                key={item.id}
                id={item.id}
                textValue={item.textValue}
                className={styles.addressItem}
              >
                <HighlightText
                  style="input/text-field"
                  highlight={props.inputValue}
                >
                  {item.description}
                </HighlightText>
              </SelectItem>
            )}
          </_ListBox>
          <_GoogleFooter />
        </_Popover>
      </_ComboBox>
    </div>
  );
}

const styles = {
  header: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingLeft: "var(--space-1)",
    paddingRight: "var(--space-05)",
  }),
  addressItem: css({ paddingLeft: "var(--space-2-5)" }),
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
};
